import { isAddress } from "ethers/lib/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import infoIcon from "../../../assets/svg/info.svg";
import { GetAccount200ResponseCollateralsInner } from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import { ChainIdEnum } from "../../../enums/chain";
import useWallet, { ID_TO_CHAINS } from "../../../hooks/wallet/useWallet";
import {
  supportedChainId,
  supportedSigningChainIds,
} from "../../../utils/wallet/connectors";
import { getCurrentChainConfig, getUsdcDecimals } from "../../../config/chains";
import { collateralAssetDecimals } from "../../../utils/asset/assets";
import { Button, ButtonThemeEnum } from "../../Buttons/styles";
import { InfoRow } from "../../ConnectWalletModal/Deposit/style";
import { Input } from "../../shared/Input";
import ModalOverlayInfo from "../../shared/ModalOverlayInfo";
import { OverlayInfoContent } from "../../shared/ModalOverlayInfo/style";
import {
  ButtonsContainer,
  ErrorMessage,
  Footer,
  InputContainer,
  InputError,
  InputLabel,
} from "../style";
import { getDepositTokenInfo } from "../../../utils/spotCollateral";
import { IFormFieldValues, IInitialStateProps } from "./InitialStateModal.types";
import {
  computeCollateralInfo,
  transferMaxResult,
} from "./InitialStateModal.helpers";
import {
  assetDisplayDecimals,
  formatFlooredAmount,
} from "../WithdrawModal.helpers";
import AmountField from "./InitialStateModal.AmountField";

function InitialStateModal({
  accountData,
  handleTransfer,
  defaultTransferData,
  asset,
  setAsset,
}: IInitialStateProps) {
  const { chainId, setChainToSwitch } = useWallet();
  const { t } = useTranslation("app", {
    keyPrefix: "DepositWithdrawModal.TransferModal.InitialStateModal",
  });
  const { t: formError } = useTranslation("formErrors");
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
    clearErrors,
    trigger,
  } = useForm<IFormFieldValues>({
    mode: "onChange",
    defaultValues: defaultTransferData,
  });

  const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);

  const [showHelpInfo, setShowHelpInfo] = useState(false);
  const [depositWithdrawError, setDepositWithdrawError] = useState<
    string | undefined
  >(t("deposit_failed"));
  const chainConfig = getCurrentChainConfig();
  const chainName = chainConfig.name;
  const tokenInfo = useMemo(
    () => getDepositTokenInfo(chainConfig.chainId, asset),
    [asset, chainConfig.chainId]
  );

  // Same decimals ConfirmTransferModal parses the amount with.
  const assetDecimals = collateralAssetDecimals[asset] ?? getUsdcDecimals();
  const displayDecimals = assetDisplayDecimals(asset);

  const collateralInfo: GetAccount200ResponseCollateralsInner | undefined =
    useMemo(
      () => computeCollateralInfo(accountData, asset, tokenInfo?.address),
      [
        accountData?.available_balance,
        accountData?.balance,
        accountData?.collaterals,
        asset,
        tokenInfo?.address,
      ]
    );

  // Reset errors
  useEffect(() => {
    clearErrors();
    setDepositWithdrawError(undefined);
    setShowHelpInfo(false);
  }, [clearErrors]);

  // One answer for the field, MAX and its reason. transferMax IS the withdraw
  // max: a transfer leaves this account just as a withdrawal does.
  const transferMax = useMemo(
    () => transferMaxResult(accountData, asset, collateralInfo, assetDecimals),
    [accountData, asset, assetDecimals, collateralInfo]
  );
  const transferrableBalance = transferMax.ok ? transferMax.max : undefined;
  const transferUnavailableReason = transferMax.ok ? undefined : transferMax.reason;

  const submitButtonContent = useMemo(() => {
    if (!chainId || !supportedSigningChainIds.includes(chainId)) {
      return (
        <Button
          fullWidth
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          type="button"
          onClick={() => setChainToSwitch(supportedChainId)}
        >
          {t("switch_to", {
            chain: ID_TO_CHAINS[supportedChainId as ChainIdEnum],
          })}
        </Button>
      );
    }

    return (
      <>
        <Button
          fullWidth
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          type={"submit"}
          disabled={!isValid}
        >
          {t("next")}
        </Button>
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          type={"button"}
          onClick={() => setShowHelpInfo(true)}
        >
          <img src={infoIcon} alt="info" />
        </Button>
      </>
    );
  }, [chainId, setChainToSwitch, isValid, t]);

  const onMaxClick = useCallback(() => {
    // An unknown maximum has no MAX: the button is disabled, and it must not
    // fill in 0 either — which is what "?? 0" used to do.
    if (transferrableBalance === undefined) return;
    clearErrors("amount");
    setValue("amount", transferrableBalance);
    trigger("amount");
  }, [clearErrors, setValue, transferrableBalance, trigger]);

  const onSubmit: SubmitHandler<IFormFieldValues> = useCallback(
    async (data) => {
      const { address, amount } = data;
      if (address && amount) {
        setDepositWithdrawError(undefined);
        handleTransfer({ address, amount });
      }
    },
    [handleTransfer]
  );

  return (
    <div>
      <ModalOverlayInfo
        show={showHelpInfo}
        onClose={() => setShowHelpInfo(false)}
        height="210px"
      >
        <OverlayInfoContent>
          <span>{t("transfers")}</span>
          <span>{t("transfers_desc")}</span>
        </OverlayInfoContent>
      </ModalOverlayInfo>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputContainer>
          <InputLabel>{t("recipient_address", { chainName })}</InputLabel>
          <Input
            placeholder="0x56b...6405"
            type="text"
            {...register("address", {
              required: true,
              validate: {
                isAddress: (v) => isAddress(v || ""),
                transferToSelf: (v) =>
                  !accountData?.account ||
                  (v || "").toLowerCase() !== accountData.account.toLowerCase(),
              },
            })}
            error={!!errors.address?.type}
          />
          {errors.address?.type === "required" && (
            <InputError>{formError("address.required")}</InputError>
          )}
          {errors.address?.type === "isAddress" && (
            <InputError>{formError("address.isAddress")}</InputError>
          )}
          {errors.address?.type === "transferToSelf" && (
            <InputError>{formError("address.transferToSelf")}</InputError>
          )}
        </InputContainer>
        <AmountField
          t={t}
          formError={formError}
          register={register}
          errors={errors}
          asset={asset}
          setAsset={setAsset}
          chainId={chainId}
          assetDropdownOpen={assetDropdownOpen}
          setAssetDropdownOpen={setAssetDropdownOpen}
          onMaxClick={onMaxClick}
          transferrableBalance={transferrableBalance}
          transferUnavailableReason={transferUnavailableReason}
          assetDecimals={assetDecimals}
        />
        <InfoRow>
          <span>{t("exchange_balance")}</span>
          <span>
            {collateralInfo
              ? formatFlooredAmount(collateralInfo.balance, displayDecimals)
              : "---"}
          </span>
        </InfoRow>
        <InfoRow>
          <span
            style={
              errors.amount?.type === "lessThanEqualToTransferableBalance"
                ? { color: COLORS.negative.one }
                : undefined
            }
          >
            {t("transferable_balance")}
          </span>
          <span
            style={
              errors.amount?.type === "lessThanEqualToTransferableBalance"
                ? { color: COLORS.negative.one }
                : undefined
            }
          >
            {transferrableBalance !== undefined
              ? formatFlooredAmount(transferrableBalance, displayDecimals)
              : "---"}
          </span>
        </InfoRow>
        {depositWithdrawError && (
          <ErrorMessage>{depositWithdrawError}</ErrorMessage>
        )}
        <ButtonsContainer
          style={{
            paddingTop: SPACING.three,
          }}
        >
          {submitButtonContent}
        </ButtonsContainer>
        <Footer>{t("recipient_address_access_desc", { chainName })}</Footer>
      </form>
    </div>
  );
}

export default InitialStateModal;
